import { FC, useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import {
  Video, UserCircle, Volume2, MessageSquare, Search, Check, X, Play, Pause,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import Spinner from '@/components/Spinner';
import appLogger from '@/utils/logger';
import { useGetBotData } from '@/hooks/useBot';
import { askIOTApiFetch } from '@/utils/fetch-API';
import { getPublicApiBaseUrl } from '@/utils/publicApiBaseUrl';
import {
  SectionCard, StickyActionBar, ToggleRow, FieldLabel, InfoBanner, TabShell,
} from './shared';

interface AvatarSettingsProps {
  token: string;
}

interface PublicAvatar {
  avatar_id: string;
  avatar_name: string;
  preview_image_url?: string;
  gender?: string;
}

interface Voice {
  voice_id: string;
  name: string;
  language?: string;
  gender?: string;
  preview_audio_url?: string;
}

const AvatarSettings: FC<AvatarSettingsProps> = ({ token }) => {
  const queryClient = useQueryClient();
  const { data: botData, isLoading: isLoadingBot } = useGetBotData(token);

  const [enabled, setEnabled] = useState(false);
  const [avatarId, setAvatarId] = useState<string>('');
  const [voiceId, setVoiceId] = useState<string>('');
  const [greeting, setGreeting] = useState('');
  const [saving, setSaving] = useState(false);

  // Last-saved snapshot, so Save/Reset can be gated on dirty state like the other tabs.
  const [saved, setSaved] = useState({ enabled: false, avatarId: '', voiceId: '', greeting: '' });

  const [avatars, setAvatars] = useState<PublicAvatar[]>([]);
  const [voices, setVoices] = useState<Voice[]>([]);
  const [loadingAvatars, setLoadingAvatars] = useState(false);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // false = HEYGEN_LIVEAVATAR_API_KEY not set on server (feature completely disabled)
  const [featureConfigured, setFeatureConfigured] = useState(true);

  const [avatarSearch, setAvatarSearch] = useState('');
  const [creditBalance, setCreditBalance] = useState<number | null>(null);

  // Load current settings from bot data
  useEffect(() => {
    if (botData?.data) {
      const bot = botData.data;
      const next = {
        enabled: bot.avatar_enabled || false,
        avatarId: bot.avatar_id || '',
        voiceId: bot.avatar_voice_id || '',
        greeting: bot.avatar_greeting || '',
      };
      setEnabled(next.enabled);
      setAvatarId(next.avatarId);
      setVoiceId(next.voiceId);
      setGreeting(next.greeting);
      setSaved(next);
    }
  }, [botData]);

  // Fetch credit balance
  useEffect(() => {
    if (!enabled || !token) return;
    const fetchBalance = async () => {
      try {
        const data = await askIOTApiFetch(
          `${getPublicApiBaseUrl()}/private/chatbot/avatar/balance?chatToken=${encodeURIComponent(token)}`,
        );
        setCreditBalance(data.video_avatar_credit_balance ?? 0);
      } catch (err) {
        appLogger.error('Failed to fetch avatar balance', { data: err });
      }
    };
    fetchBalance();
  }, [enabled, token]);

  // Fetch public avatars
  useEffect(() => {
    const fetchAvatars = async () => {
      setLoadingAvatars(true);
      try {
        const res = await fetch(`${getPublicApiBaseUrl()}/public/chatbot/avatar/public-avatars`);
        if (res.status === 503) {
          // HEYGEN_LIVEAVATAR_API_KEY not configured on server — feature is disabled
          setFeatureConfigured(false);
          return;
        }
        if (res.ok) {
          const json = await res.json();
          const raw = json?.data?.results ?? (Array.isArray(json) ? json : []);
          setAvatars(
            raw.map((a: any) => ({
              avatar_id: a.avatar_id ?? a.id,
              avatar_name: a.avatar_name ?? a.name,
              preview_image_url: a.preview_image_url ?? a.preview_url,
              gender: a.gender,
            })),
          );
        }
      } catch (err) {
        appLogger.error('Failed to fetch avatars', { data: err });
        toast.error('Could not load avatars. Please refresh to try again.');
      } finally {
        setLoadingAvatars(false);
      }
    };
    fetchAvatars();
  }, []);

  // Fetch voices
  useEffect(() => {
    const fetchVoices = async () => {
      setLoadingVoices(true);
      try {
        const res = await fetch(`${getPublicApiBaseUrl()}/public/chatbot/avatar/voices`);
        if (res.ok) {
          const json = await res.json();
          const raw = json?.data?.results ?? (Array.isArray(json) ? json : []);
          setVoices(
            raw.map((v: any) => ({
              voice_id: v.voice_id ?? v.id,
              name: v.name,
              language: v.language,
              gender: v.gender,
              preview_audio_url: v.preview_audio_url || '',
            })),
          );
        }
      } catch (err) {
        appLogger.error('Failed to fetch voices', { data: err });
        toast.error('Could not load voices. Please refresh to try again.');
      } finally {
        setLoadingVoices(false);
      }
    };
    fetchVoices();
  }, []);

  const selectedAvatar = useMemo(
    () => avatars.find((a) => a.avatar_id === avatarId) || null,
    [avatars, avatarId],
  );

  const filteredAvatars = useMemo(() => {
    if (!avatarSearch.trim()) return avatars;
    const q = avatarSearch.toLowerCase();
    return avatars.filter(
      (a) =>
        a.avatar_name?.toLowerCase().includes(q) ||
        a.gender?.toLowerCase().includes(q),
    );
  }, [avatars, avatarSearch]);

  const isDirty = enabled !== saved.enabled
    || avatarId !== saved.avatarId
    || voiceId !== saved.voiceId
    || greeting !== saved.greeting;

  const handleReset = () => {
    setEnabled(saved.enabled);
    setAvatarId(saved.avatarId);
    setVoiceId(saved.voiceId);
    setGreeting(saved.greeting);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await askIOTApiFetch(
        `${getPublicApiBaseUrl()}/private/chatbot/update`,
        {
          chat_token: token,
          avatarEnabled: enabled,
          avatarId: avatarId || null,
          avatarVoiceId: voiceId || null,
          avatarGreeting: greeting || null,
          avatarImageUrl: selectedAvatar?.preview_image_url || null,
        },
        'POST',
      );
      queryClient.invalidateQueries();
      setSaved({ enabled, avatarId, voiceId, greeting });
      toast.success('Avatar settings saved');
    } catch {
      toast.error('Failed to save avatar settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleVoicePreview = async () => {
    if (playingVoiceId === voiceId && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setPlayingVoiceId(null);
      return;
    }
    if (audioRef.current) {
      audioRef.current.pause();
    }
    try {
      setPlayingVoiceId(voiceId);

      // Check if voice already has a preview URL (ElevenLabs voices include it)
      const selectedVoice = voices.find((v) => v.voice_id === voiceId);
      let audioUrl: string | null = selectedVoice?.preview_audio_url || null;

      if (!audioUrl) {
        // Fall back to fetching from the backend preview endpoint
        const res = await fetch(`${getPublicApiBaseUrl()}/public/chatbot/avatar/voices/${voiceId}/preview`);
        if (!res.ok) throw new Error('Failed to fetch preview');
        const data = await res.json();

        if (data.preview_url) {
          // ElevenLabs: direct URL
          audioUrl = data.preview_url;
        } else if (data.audio_base64) {
          // HeyGen: base64 encoded audio — convert to Blob URL
          const byteChars = atob(data.audio_base64);
          const byteArray = new Uint8Array(byteChars.length);
          for (let i = 0; i < byteChars.length; i++) {
            byteArray[i] = byteChars.charCodeAt(i);
          }
          const blob = new Blob([byteArray], { type: 'audio/mpeg' });
          audioUrl = URL.createObjectURL(blob);
        }
      }

      if (!audioUrl) throw new Error('No preview audio available');

      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      audio.onended = () => {
        setPlayingVoiceId(null);
        if (audioUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(audioUrl);
        }
      };
      audio.play().catch((e) => {
        appLogger.error('Voice preview play failed', { data: e });
        setPlayingVoiceId(null);
      });
    } catch (err) {
      appLogger.error('Voice preview error', { data: err });
      setPlayingVoiceId(null);
    }
  };

  if (isLoadingBot) {
    return (
      <div className="flex justify-center py-10">
        <Spinner />
      </div>
    );
  }

  if (!featureConfigured) {
    return (
      <div className="p-6">
        <SectionCard
          icon={Video}
          title="Video Avatar"
          subtitle="Enable a live AI video avatar that greets visitors and speaks chatbot responses."
        >
          <InfoBanner variant="warning" title="Feature not configured">
            The HeyGen LiveAvatar API key (
            <code className="font-mono text-xs">HEYGEN_LIVEAVATAR_API_KEY</code>) is not set on the
            server. Add it to the workspace secrets (Supabase Vault) to enable this feature.
          </InfoBanner>
        </SectionCard>
      </div>
    );
  }

  return (
    <TabShell
      footer={(
        <StickyActionBar
          onReset={handleReset}
          onSave={handleSave}
          saving={saving}
          saveDisabled={!isDirty}
          resetDisabled={saving || !isDirty}
          saveLabel="Save Settings"
        />
      )}
    >
      <div className="space-y-6">
        {/* Enable */}
        <SectionCard
          icon={Video}
          title="Video Avatar"
          subtitle="Enable a live AI video avatar that greets visitors and speaks chatbot responses."
          bodyClassName="px-6"
        >
          <div className="divide-y divide-gray-100">
            <ToggleRow
              label="Enable video avatar"
              description="When on, visitors can start a live video conversation with an AI avatar."
              checked={enabled}
              onCheckedChange={setEnabled}
            />
          </div>

          {enabled && (
            <div className="space-y-3 pb-5">
              <InfoBanner variant="info">
                Video Avatar consumes Video Avatar Credits (1 credit/minute).{' '}
                <a href="/settings?tab=billing-and-usage" className="font-medium underline hover:opacity-80">
                  Buy credits &rarr;
                </a>
              </InfoBanner>
              {creditBalance === 0 && (
                <InfoBanner variant="warning">
                  Your workspace has 0 video avatar credits. Visitors won&apos;t be able to use the
                  avatar until credits are purchased.
                </InfoBanner>
              )}
            </div>
          )}
        </SectionCard>

        {enabled && (
          <>
            {/* Avatar selection */}
            <SectionCard
              icon={UserCircle}
              title="Avatar"
              subtitle="Choose the avatar visitors will see and talk to."
            >
              {/* Selected avatar preview */}
              {selectedAvatar && (
                <div className="flex items-center gap-4 rounded-lg border border-primary/30 bg-primary/[0.06] p-3">
                  <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-lg">
                    {selectedAvatar.preview_image_url ? (
                      <Image
                        src={selectedAvatar.preview_image_url}
                        alt={selectedAvatar.avatar_name}
                        fill
                        className="object-cover"
                        unoptimized
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-gray-100 text-lg font-medium text-gray-500">
                        {selectedAvatar.avatar_name?.charAt(0) || '?'}
                      </div>
                    )}
                    <span className="absolute -right-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary shadow-sm">
                      <Check className="h-3 w-3 text-white" />
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900">{selectedAvatar.avatar_name}</p>
                    {selectedAvatar.gender && (
                      <p className="text-xs capitalize text-gray-500">{selectedAvatar.gender}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setAvatarId('')}
                    className="shrink-0 rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                    title="Remove selection"
                    aria-label="Remove avatar selection"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}

              {loadingAvatars ? (
                <div className="flex justify-center py-6">
                  <Spinner />
                </div>
              ) : avatars.length > 0 ? (
                <>
                  {/* Search */}
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <Input
                      placeholder="Search avatars..."
                      value={avatarSearch}
                      onChange={(e) => setAvatarSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>

                  {/* Avatar grid */}
                  <div className="grid max-h-[340px] grid-cols-3 gap-2 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50/60 p-3 sm:grid-cols-4 lg:grid-cols-5">
                    {filteredAvatars.map((avatar) => {
                      const isSelected = avatarId === avatar.avatar_id;
                      return (
                        <button
                          key={avatar.avatar_id}
                          type="button"
                          className={`group relative flex flex-col overflow-hidden rounded-lg border-2 bg-white transition-all ${
                            isSelected
                              ? 'border-primary shadow-md ring-2 ring-primary/20'
                              : 'border-transparent hover:border-gray-200 hover:shadow-sm'
                          }`}
                          onClick={() => setAvatarId(avatar.avatar_id)}
                        >
                          <div className="relative aspect-square w-full overflow-hidden bg-gray-100">
                            {avatar.preview_image_url ? (
                              <Image
                                src={avatar.preview_image_url}
                                alt={avatar.avatar_name}
                                fill
                                className="object-cover object-top transition-transform group-hover:scale-105"
                                unoptimized
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center text-2xl font-medium text-gray-400">
                                {avatar.avatar_name?.charAt(0) || '?'}
                              </div>
                            )}
                            {isSelected && (
                              <span className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-primary shadow">
                                <Check className="h-3.5 w-3.5 text-white" />
                              </span>
                            )}
                          </div>
                          <div className="px-2 py-2">
                            <p className={`truncate text-xs font-medium ${isSelected ? 'text-primary' : 'text-gray-900'}`}>
                              {avatar.avatar_name}
                            </p>
                          </div>
                        </button>
                      );
                    })}

                    {filteredAvatars.length === 0 && (
                      <div className="col-span-full flex flex-col items-center justify-center py-8 text-sm text-gray-500">
                        <Search className="mb-2 h-8 w-8 opacity-40" />
                        No avatars match &ldquo;{avatarSearch}&rdquo;
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-500">
                  No avatars available. Check your HeyGen API key configuration.
                </p>
              )}

              <p className="text-xs text-gray-500">
                Want a custom avatar? Contact{' '}
                <a href="mailto:support@marketbetter.ai" className="underline hover:text-gray-700">
                  support@marketbetter.ai
                </a>
              </p>
            </SectionCard>

            {/* Voice selection */}
            <SectionCard
              icon={Volume2}
              title="Voice"
              subtitle="The voice the avatar speaks with. Preview before you choose."
            >
              <FieldLabel hint="Pick from the available text-to-speech voices.">Voice</FieldLabel>
              {loadingVoices ? (
                <Spinner />
              ) : (
                <div className="flex items-center gap-2">
                  <Select value={voiceId} onValueChange={setVoiceId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a voice..." />
                    </SelectTrigger>
                    <SelectContent>
                      {voices.map((voice) => (
                        <SelectItem key={voice.voice_id} value={voice.voice_id}>
                          {voice.name}
                          {voice.language ? ` (${voice.language})` : ''}
                          {voice.gender ? ` — ${voice.gender}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {voiceId && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="shrink-0"
                      onClick={toggleVoicePreview}
                      title={playingVoiceId === voiceId ? 'Stop preview' : 'Play voice sample'}
                      aria-label={playingVoiceId === voiceId ? 'Stop preview' : 'Play voice sample'}
                    >
                      {playingVoiceId === voiceId ? (
                        <Pause className="h-4 w-4" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                </div>
              )}
            </SectionCard>

            {/* Greeting */}
            <SectionCard
              icon={MessageSquare}
              title="Greeting"
              subtitle="The avatar speaks this message when the chatbot first opens."
            >
              <Textarea
                placeholder="Hi! I'm your AI assistant. How can I help you today?"
                value={greeting}
                onChange={(e) => setGreeting(e.target.value)}
                rows={3}
              />
            </SectionCard>
          </>
        )}
      </div>
    </TabShell>
  );
};

export default AvatarSettings;
